import type { BasicProductData } from 'components/layout/modals/compatible-combinations-browser-modal';
import type {
  ConfigurationType,
  ExistingSystemComponentType,
  ExistingSystemType,
  LevelType,
  SelectedSystemType,
  SizingBandsSettingType
} from 'data/types';
import {
  buildSelectedSystemFromCompatibility,
  type FullProductData
} from 'utils/compatibility-systems';
import type { DuctedCoverageType } from 'utils/ducted-coverage';
import { buildDuctedLayoutConfiguration } from 'utils/ducted-layout-configuration';
import { parseDisplayAttributes } from 'utils/ducted-ratings';
import { readDuctedDesignCoverage } from 'utils/ducted-system-metrics';
import {
  COMPONENT_TYPE_TO_POSITION,
  keptEquipmentPositionSlots
} from 'utils/existing-equipment';
import { deriveExistingEquipmentSize } from 'utils/existing-equipment-size';
import { resolveOpenSlotPlaceholderLabels } from 'utils/open-slot-placeholder-labels';
import { buildSelectedCombination } from 'utils/quotable-add-pipeline';

export type BuildDuctedDetailDisplayParams = {
  combination: Record<string, unknown>;
  productData: Record<string, BasicProductData>;
  cardId: string;
  systemUniqueId: string;
  typeLabel?: string;
  keptPairing?: boolean;
  displayTypeLabel?: string;
  components: ExistingSystemComponentType[];
  existingSystem: ExistingSystemType | null;
  structure: LevelType[];
  sizingBands: SizingBandsSettingType | null;
  totalBTUs: { heating: number; cooling: number };
};

export type DuctedDetailDisplay = {
  system: SelectedSystemType;
  layoutConfiguration: ConfigurationType | null;
};

function readFiniteNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function readPositiveNumber(value: unknown): number | null {
  const parsed = readFiniteNumber(value);
  return parsed != null && parsed > 0 ? parsed : null;
}

function toFullProductData(
  product: BasicProductData,
  heatingCapacityBtu?: number | null
): FullProductData {
  return {
    productId: product.productId,
    manufacturer: product.manufacturer,
    modelNumber: product.modelNumber,
    name: product.name,
    description: product.description,
    price: product.price,
    isOnSale: product.isOnSale,
    weight: null,
    productUrl: product.productUrl,
    photo: product.photo,
    fulfillableStock: product.fulfillableStock,
    series: null,
    seriesDescription: null,
    seriesCode: null,
    height: null,
    width: null,
    depth: null,
    tonnage: null,
    stagesOfCool: null,
    stagesOfHeat: null,
    compressorType: null,
    soundRating: null,
    refrigerant: null,
    mobileHomeCompat: null,
    afue: null,
    blowerType: null,
    maxHeatOutput:
      heatingCapacityBtu != null ? String(heatingCapacityBtu) : null,
    cabinetSize: null,
    orientation: null,
    productType: null,
    voltage: null,
    amperage: null,
    warranties: []
  };
}

function readComponentHeatingCapacity(
  component: ExistingSystemComponentType | undefined
): number | null {
  if (!component) return null;
  const raw = (component as unknown as Record<string, unknown>).heatingCapacity;
  return readPositiveNumber(raw);
}

function resolveSlotProduct(
  slot: {
    manufacturer: string;
    modelNumber: string;
    productId: number | null;
  } | null,
  productData: Record<string, BasicProductData>,
  heatingCapacityBtu?: number | null
): FullProductData | null {
  if (!slot) return null;
  const product = productData[`${slot.manufacturer}|${slot.modelNumber}`];
  if (product) {
    return toFullProductData(product, heatingCapacityBtu);
  }
  return {
    productId: slot.productId ?? 0,
    manufacturer: slot.manufacturer,
    modelNumber: slot.modelNumber,
    name: `${slot.manufacturer} ${slot.modelNumber}`,
    description: null,
    price: null,
    isOnSale: false,
    weight: null,
    productUrl: null,
    photo: null,
    fulfillableStock: 0,
    series: null,
    seriesDescription: null,
    seriesCode: null,
    height: null,
    width: null,
    depth: null,
    tonnage: null,
    stagesOfCool: null,
    stagesOfHeat: null,
    compressorType: null,
    soundRating: null,
    refrigerant: null,
    mobileHomeCompat: null,
    afue: null,
    blowerType: null,
    maxHeatOutput:
      heatingCapacityBtu != null ? String(heatingCapacityBtu) : null,
    cabinetSize: null,
    orientation: null,
    productType: null,
    voltage: null,
    amperage: null,
    warranties: []
  };
}

export function buildDuctedDetailDisplay({
  combination,
  productData,
  cardId,
  systemUniqueId,
  typeLabel,
  keptPairing,
  displayTypeLabel,
  components,
  existingSystem,
  structure,
  sizingBands,
  totalBTUs
}: BuildDuctedDetailDisplayParams): DuctedDetailDisplay {
  const systemTypeLabel =
    typeLabel ||
    (typeof combination.SystemTypeLabel === 'string'
      ? combination.SystemTypeLabel
      : 'Ducted System');
  const combo = buildSelectedCombination(
    combination,
    productData,
    keptPairing,
    displayTypeLabel
  );
  const displayAttributes = parseDisplayAttributes(combination);
  const coolingCapacity =
    readPositiveNumber(combo.coolingCapacity) ??
    displayAttributes.derived.coolingCapacity?.value ??
    null;
  const heatingCapacity =
    readPositiveNumber(combo.heatingCapacity) ??
    displayAttributes.derived.heatingCapacity?.value ??
    null;
  const keptBlowerComponent = components.find(
    (component) =>
      COMPONENT_TYPE_TO_POSITION[component.type] === 'blower' &&
      !component.replacing
  );
  const keptBlower = keptBlowerComponent
    ? {
        label: keptBlowerComponent.type,
        heatingCapacityBtu: readComponentHeatingCapacity(keptBlowerComponent)
      }
    : null;
  const outdoorProduct = resolveSlotProduct(combo.outdoor, productData);
  const indoorProduct = resolveSlotProduct(combo.indoor, productData);
  const blowerProduct = resolveSlotProduct(
    combo.blower,
    productData,
    heatingCapacity
  );
  const replacingProductIds = new Set<number>();
  if (outdoorProduct) replacingProductIds.add(outdoorProduct.productId);
  if (indoorProduct) replacingProductIds.add(indoorProduct.productId);
  if (blowerProduct) replacingProductIds.add(blowerProduct.productId);
  const openSlotLabels = resolveOpenSlotPlaceholderLabels({
    combo,
    systemTypeLabel,
    keptSlots: keptEquipmentPositionSlots(components),
    keptBlower
  });
  // The loads the zone table matches against: the ones the server divided
  // by for the row's coverage percents, whatever basis resolved them (a
  // room-by-room calculation, the structure's design loads, the selected
  // tonnage, a home-details estimate) — so the table shows a requirement
  // whenever the card shows a percent. The project's Layout & Sizing totals
  // stand in only for a row that carries none.
  const designCoverage = readDuctedDesignCoverage(combination);
  const heatingRequirementBtu =
    designCoverage?.heatingRequirementBtu ??
    (totalBTUs.heating > 0 ? totalBTUs.heating : null);
  const coolingRequirementBtu =
    designCoverage?.coolingRequirementBtu ??
    (totalBTUs.cooling > 0 ? totalBTUs.cooling : null);
  let ductedCoverage: DuctedCoverageType | null = null;
  if (designCoverage) {
    ductedCoverage = {
      ...designCoverage,
      heatingRequirementBtu,
      coolingRequirementBtu
    };
  } else if (heatingRequirementBtu != null || coolingRequirementBtu != null) {
    ductedCoverage = {
      heatingCoveragePercent: null,
      coolingCoveragePercent: null,
      basis: 'design_loads',
      heatingRequirementBtu,
      coolingRequirementBtu
    };
  }
  const system = buildSelectedSystemFromCompatibility({
    quotableSystemUniqueId: cardId,
    configurationUniqueId: `cs-layout-${cardId}`,
    configurationLabel: `${systemTypeLabel} Layout`,
    systemTypeLabel,
    combinationPerformance: {
      seer2: readFiniteNumber(combo.seer2),
      eer2: readFiniteNumber(combo.eer2),
      hspf2: readFiniteNumber(combo.hspf2),
      coolingCapacity,
      heatingCapacity,
      afue: readFiniteNumber(combo.afue)
    },
    outdoorProduct,
    indoorProduct,
    blowerProduct,
    replacingProductIds,
    ...openSlotLabels,
    keptPairing: combo.keptPairing,
    displayName: combo.displayName,
    ductedCoverage,
    sizingBands,
    existingSystemUniqueId: systemUniqueId
  });
  const layout = buildDuctedLayoutConfiguration({
    system,
    existingSystem,
    structure
  });
  return {
    system,
    layoutConfiguration: layout
  };
}

export function existingSizeForDisplay(
  existingSystem: ExistingSystemType | null
) {
  return deriveExistingEquipmentSize(existingSystem);
}
